/** --------------------------------------------------------
* Author Tien Tran
* Email tientran0019@gmail.com
* Phone 0972970075
*
* Created: 2024-06-06 10:13:49

* Last updated on: 2024-06-06 10:13:49
* Last updated by: Tien Tran
*------------------------------------------------------- */

"use client";

import React, { ReactNode } from "react";

import Link from "next/link";

import { Theme } from "@mui/material/styles";
import { Box, Stack, SxProps, Tooltip, StackProps } from "@mui/material";

import TextMaxLine, { TextMaxLineProps } from "#src/components/text-max-line";

import Avatar from "../avatar";

interface PropTypes {
	src?: string | null;
	size?: number;
	title: string;
	href?: string | null;
	subTitle?: string | null;
	rightIcon?: string | ReactNode | null;
	avatarSx?: SxProps<Theme>;
	titleSx?: SxProps<Theme>;
	subTitleSx?: SxProps<Theme>;
	titleProps?: Partial<TextMaxLineProps>;
	subTitleProps?: Partial<TextMaxLineProps>;
}

const AvatarBlock: React.FC<PropTypes & StackProps> = (props) => {
	const {
		src,
		href,
		title,
		subTitle,
		rightIcon,
		avatarSx,
		titleSx,
		subTitleSx,
		size = 40,
		titleProps,
		subTitleProps,
		...attrs
	} = props;

	const Wrapper = React.useMemo(() => {
		return href ? Link : Box;
	}, [href]);

	return (
		<Wrapper href={href!} className="w-auto block">
			<Stack
				direction="row"
				spacing={1}
				alignItems="center"
				minWidth={0}
				maxWidth="100%"
				{...attrs}
			>
				<Box
					sx={{
						border: (theme) => `solid 2px ${theme.palette.common.white}`,
						...avatarSx,
						height: size + 3 + "px",
						width: size + 3 + "px",
						borderRadius: size / 2,
					}}
				>
					<Avatar src={src ?? ""} alt={title} size={size} />
				</Box>
				<Stack
					flex={1}
					minWidth={0}
					overflow="hidden"
					alignItems="start"
					maxWidth="100%"
				>
					<Stack
						direction="row"
						spacing={2}
						minWidth={0}
						overflow="hidden"
						maxWidth="100%"
						alignItems="center"
					>
						<Tooltip title={title} arrow>
							<TextMaxLine
								{...titleProps}
								line={1}
								sx={{
									fontSize: "14px",
									fontWeight: 500,
									display: "inline-block",
									whiteSpace: "pre",
									width: "100%",
									...titleSx,
									flex: 1,
								}}
							>
								{title}
							</TextMaxLine>
						</Tooltip>
						{rightIcon}
					</Stack>
					{subTitle && (
						<Tooltip title={subTitle} arrow>
							<TextMaxLine
								{...subTitleProps}
								line={1}
								sx={{
									fontSize: "12px",
									lineHeight: "14px",
									color: "#7A909E",
									display: "block",
									whiteSpace: "pre",
									maxWidth: "100%",
									...subTitleSx,
								}}
							>
								{subTitle}
							</TextMaxLine>
						</Tooltip>
					)}
				</Stack>
			</Stack>
		</Wrapper>
	);
};

export default React.memo(AvatarBlock);
